import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Check, X } from 'lucide-react';

export default function ToneOfVoice() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Tone of Voice
        </h1>
        <p className="text-base text-body leading-relaxed">
          Our voice reflects technical expertise, direct communication, and reliable partnership.
        </p>
      </div>

      <Card className="p-8 bg-light-blue border-jackstone-blue/20">
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-3">
          Expert, Direct, and Reliable
        </h2>
        <p className="text-body">
          The brand's voice is defined by a commitment to technical precision and professional
          partnership. We aim to instill confidence in our capabilities and the reliability of
          our products and services.
        </p>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Voice Framework
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <Badge className="mb-3">Core Voice</Badge>
            <h3 className="text-xl font-heading font-semibold text-body mb-2">
              Expert and Knowledgeable
            </h3>
            <p className="text-body mb-4">
              Always convey deep industry understanding, particularly in technical documentation,
              sales materials, and service communications.
            </p>
            <div className="bg-surface p-4 rounded text-sm text-subtle italic">
              "Our plate freezers are designed with F51 Duplex rods and 6063 aluminium plates,
              ensuring exceptional durability and energy efficiency for demanding industrial applications."
            </div>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Tone</Badge>
            <h3 className="text-xl font-heading font-semibold text-body mb-2">
              Direct and Clear
            </h3>
            <p className="text-body mb-4">
              Maintain a concise, action-oriented, and highly professional tone. Avoid unnecessary
              fluff or overly casual language.
            </p>
            <div className="bg-surface p-4 rounded text-sm text-subtle italic">
              "Let's schedule a technical consultation to discuss your freezing requirements.
              Our engineering team will assess your capacity needs and recommend the optimal solution."
            </div>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Personality</Badge>
            <h3 className="text-xl font-heading font-semibold text-body mb-2">
              Reliable and Assuring
            </h3>
            <p className="text-body mb-4">
              Build trust by emphasising exceptional durability and long-term reliability.
              Assure clients of a hassle-free experience.
            </p>
            <div className="bg-surface p-4 rounded text-sm text-subtle italic">
              "Since 1937, our freezers have operated continuously in the harshest industrial
              environments. Your investment is protected by our proven track record and comprehensive support."
            </div>
          </Card>

          <Card className="p-6">
            <Badge className="mb-3">Purpose</Badge>
            <h3 className="text-xl font-heading font-semibold text-body mb-2">
              Inform and Assure
            </h3>
            <p className="text-body mb-4">
              The primary goal is to inform stakeholders with accuracy and assure them of
              continued process and support.
            </p>
            <div className="bg-surface p-4 rounded text-sm text-subtle italic">
              "We'll provide detailed specifications, installation timelines, and ongoing
              maintenance schedules to ensure your operation runs smoothly from day one."
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-2">
          Signature Phrases
        </h3>
        <p className="text-sm text-subtle mb-4">
          Approved, on-brand phrases drawn from Jackstone's own sales and technical literature.
          Reuse them — don't reinvent.
        </p>
        <div className="flex flex-wrap gap-2">
          <Badge tone="navy" fill="soft">Built for hard work</Badge>
          <Badge tone="navy" fill="soft">Set and forget</Badge>
          <Badge tone="navy" fill="soft">Maximum uptime</Badge>
          <Badge tone="navy" fill="soft">Exceptional durability</Badge>
          <Badge tone="navy" fill="soft">The proven choice for the industry</Badge>
          <Badge tone="navy" fill="soft">Stood the test of time</Badge>
          <Badge tone="navy" fill="soft">How it supports your operation</Badge>
          <Badge tone="navy" fill="soft">Best suited for</Badge>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Key Messaging Principles
        </h3>
        <div className="space-y-4">
          <div>
            <h4 className="font-medium text-body mb-2">Emphasize Technical Excellence</h4>
            <p className="text-sm text-subtle">
              Highlight engineering precision, material quality, and performance specifications.
              Use technical terms appropriately while remaining accessible.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Focus on Outcomes</h4>
            <p className="text-sm text-subtle">
              Articulate clear benefits: energy efficiency, reduced maintenance costs, product
              quality preservation, operational efficiency.
            </p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Build Confidence</h4>
            <p className="text-sm text-subtle">
              Reference our 85+ year history, industry certifications, customer success stories,
              and commitment to support.
            </p>
          </div>
        </div>
      </Card>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Words We Use / Words We Avoid
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6 bg-success/5 border border-success/20">
            <h4 className="font-display font-normal text-body mb-3 flex items-center gap-2">
              <Check className="h-5 w-5 text-success" /> Use These Words
            </h4>
            <div className="flex flex-wrap gap-2">
              <Badge tone="green" fill="soft">Precision</Badge>
              <Badge tone="green" fill="soft">Engineered</Badge>
              <Badge tone="green" fill="soft">Reliable</Badge>
              <Badge tone="green" fill="soft">Efficient</Badge>
              <Badge tone="green" fill="soft">Proven</Badge>
              <Badge tone="green" fill="soft">Industrial</Badge>
              <Badge tone="green" fill="soft">Technical</Badge>
              <Badge tone="green" fill="soft">Quality</Badge>
              <Badge tone="green" fill="soft">Optimised</Badge>
              <Badge tone="green" fill="soft">Robust</Badge>
            </div>
          </Card>

          <Card className="p-6 border-destructive/50 bg-destructive/5">
            <h4 className="font-display font-normal text-body mb-3 flex items-center gap-2">
              <X className="h-5 w-5 text-destructive" /> Avoid These Words
            </h4>
            <div className="flex flex-wrap gap-2">
              <Badge tone="red" fill="soft">Revolutionary</Badge>
              <Badge tone="red" fill="soft">Disruptive</Badge>
              <Badge tone="red" fill="soft">Game-changing</Badge>
              <Badge tone="red" fill="soft">Awesome</Badge>
              <Badge tone="red" fill="soft">Amazing</Badge>
              <Badge tone="red" fill="soft">Cool</Badge>
              <Badge tone="red" fill="soft">Trendy</Badge>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Example Communications
        </h3>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-body mb-1">Website Copy:</p>
            <p className="text-sm text-subtle italic">
              "Jackstone freezing systems combine over 85 years of engineering evolution with modern
              refrigeration technology to deliver proven performance and reliability for the global food industry."
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-body mb-1">Email Communication:</p>
            <p className="text-sm text-subtle italic">
              "Thank you for your enquiry. I've reviewed your requirements and believe our Vertical
              Plate Freezer (VPF) would be well suited to your application. I have a few questions to
              confirm your installation space, and I've attached some initial drawings to get us started."
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
